import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerHeaderTitle,
  Field,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  OverlayDrawer,
  Textarea,
  makeStyles,
} from '@fluentui/react-components';
import { X } from 'lucide-react';

import { ApiError } from '../api/client';
import BulkActionResultView, {
  DeviceActionResult,
  DeviceActionResultRow,
} from './BulkActionResultView';

import EnvironmentEcho from './EnvironmentEcho';
import { IDENTIFIER } from '../theme/tokens';
// Re-export types from their new home so existing consumers
// (AssignDevicesDialog) keep working without import changes.
export type { DeviceActionResult, DeviceActionResultRow };

export interface BulkDeviceActionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  submitLabel: (count: number) => string;
  submittingLabel: string;
  resetLabel: string;
  inputPlaceholder: string;
  onSubmit: (serials: string[]) => Promise<DeviceActionResult>;
  // Called on any submit whose response includes at least one successfully
  // processed device. Typically wires to react-query invalidation for the
  // migration detail query so the device grid refreshes.
  onProcessed?: (result: DeviceActionResult) => void;
}

const useStyles = makeStyles({
  // Wide right-anchored blade, consistent with the T2T assign drawer so the
  // add-devices experience reads the same across migration types. Body scrolls
  // vertically for long result lists.
  drawer: {
    width: 'min(760px, 92vw)',
    maxWidth: '92vw',
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '16px',
  },
  description: {
    fontSize: '13px',
    color: '#52525b',
  },
  countLine: {
    fontSize: '12px',
    color: '#52525b',
  },
  textarea: {
    ...IDENTIFIER,
    minHeight: '140px',
  },
  footerActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    columnGap: '8px',
  },
});

export default function BulkDeviceActionDialog({
  open,
  onOpenChange,
  title,
  description,
  submitLabel,
  submittingLabel,
  resetLabel,
  inputPlaceholder,
  onSubmit,
  onProcessed,
}: BulkDeviceActionDialogProps) {
  const styles = useStyles();
  const [input, setInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<DeviceActionResult | null>(null);
  const [error, setError] = useState<unknown>(null);

  const serials = useMemo(() => parseSerialNumbers(input), [input]);

  // Reset internal state every time the dialog is dismissed so a re-open starts
  // fresh. Fluent Dialog unmounts its contents when closed; this guard is for the
  // case where the parent keeps the component mounted across open/close cycles.
  useEffect(() => {
    if (!open) {
      setInput('');
      setIsSubmitting(false);
      setResult(null);
      setError(null);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (serials.length === 0 || isSubmitting) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const next = await onSubmit(serials);
      setResult(next);
      onProcessed?.(next);
    } catch (err) {
      setError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setInput('');
    setResult(null);
    setError(null);
  };

  return (
    <OverlayDrawer
      open={open}
      onOpenChange={(_, data) => onOpenChange(data.open)}
      position="end"
      className={styles.drawer}
    >
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button
              appearance="subtle"
              aria-label="Close"
              icon={<X size={20} strokeWidth={1.5} />}
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            />
          }
        >
          {title}
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody>
        {/* FD-422 — this drawer is the shared body behind the bulk device
            actions, so the echo lands on every one of them at once. */}
        <div className="mb-4">
          <EnvironmentEcho />
        </div>
        {result ? (
          <BulkActionResultView result={result} resetLabel={resetLabel} />
        ) : (
          <div className={styles.body}>
            <div className={styles.description}>{description}</div>
            {error ? <ErrorView error={error} /> : null}
            <Field>
              <Textarea
                value={input}
                onChange={(_, data) => setInput(data.value)}
                placeholder={inputPlaceholder}
                disabled={isSubmitting}
                className={styles.textarea}
                resize="vertical"
              />
            </Field>
            <div className={styles.countLine}>
              {serials.length === 0
                ? 'Enter one or more serial numbers, separated by newlines or commas.'
                : `${serials.length} unique device${serials.length === 1 ? '' : 's'} ready to submit.`}
            </div>
          </div>
        )}
      </DrawerBody>
      <DrawerFooter>
        <div className={styles.footerActions}>
          {result ? (
            <>
              <Button appearance="secondary" onClick={handleReset}>
                {resetLabel}
              </Button>
              <Button appearance="primary" onClick={() => onOpenChange(false)}>
                Close
              </Button>
            </>
          ) : (
            <>
              <Button
                appearance="subtle"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleSubmit}
                disabled={serials.length === 0 || isSubmitting}
              >
                {isSubmitting ? submittingLabel : submitLabel(serials.length)}
              </Button>
            </>
          )}
        </div>
      </DrawerFooter>
    </OverlayDrawer>
  );
}

function ErrorView({ error }: { error: unknown }) {
  const title =
    error instanceof ApiError ? `Request failed (${error.status})` : 'Unexpected error';
  const body =
    error instanceof Error ? error.message : 'An unknown error occurred. Please try again.';
  return (
    <MessageBar intent="error">
      <MessageBarBody>
        <MessageBarTitle>{title}</MessageBarTitle>
        {body}
      </MessageBarBody>
    </MessageBar>
  );
}

// Split on newlines or commas, trim each entry, drop empties, dedupe preserving
// insertion order. Exported so unit tests (eventually) can exercise it directly.
export function parseSerialNumbers(input: string): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of input.split(/[\n,]+/)) {
    const trimmed = raw.trim();
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    result.push(trimmed);
  }
  return result;
}
